// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';

import { useThemeId } from '@tectonics/prs/merchant';
import { getThemeById } from '../../theme';
import { Empty } from '../../atoms';

import ebanxDarkBlueSrc from './ebanx-logo-darkblue.svg';
import ebanxLogoNewSrc from './ebanx-logo-new.svg';
import padlockNewSrc from './padlock-new.svg';
import padlock from './padlock.svg';

import { MainPanelHeader } from '../../template';
import { getCustomThemeLogo } from './custom-theme-logo';
import { useIncomingPayment, getMerchant, getCountry, getSafePaymentType } from '@tectonics/prs/incoming-payment';
import { getPartnerLogoUrl } from '../../../../prs/merchant/config/get-partner-logo-url';
import { translate } from '@tectonics/translate';

export function Header(): Node {
  const themeId = useThemeId();
  const theme = getThemeById(themeId);
  const customThemeLogoSrc = getCustomThemeLogo(themeId);

  if (theme.layout.hideEbanxHeader)
    return <Empty />;

  return (
    <MainPanelHeader>
      <ContentWrapper>
        {customThemeLogoSrc ? (
          <CustomContent logo={customThemeLogoSrc} />
        ) : (
          <HeaderContent />
        )}
      </ContentWrapper>
    </MainPanelHeader>
  );
}

function PayingByMessage(): Node {
  return <>{translate('volcanes.payingByMessage')}</>;
}

function SafePageMessage(): Node {
  return translate('volcanes.safePaymentPage');
}

function CustomContent({ logo }: { logo: string }): Node {
  return <MerchantLogo src={logo} alt="theme logo" />;
}

function PartnerLogo(): Node {
  const incomingPayment = useIncomingPayment();
  const merchant = getMerchant(incomingPayment);
  const partnerLogo = getPartnerLogoUrl(merchant);
  if (partnerLogo) {
    return <PartnerSvg data-test-id="display-partner-logo" src={partnerLogo} alt={merchant.name} />;
  }
  return <></>;
}

function HeaderContent(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  const paymentType = getSafePaymentType(incomingPayment);

  switch (country) {
    case 'za':
    case 'ke':
    case 'tz':
    case 'ug':
    case 'zm':
    case 'eg':
    case 'gh':
      return <CustomHeaderContentByPaymentType paymentType={paymentType} />;
    default:
      return <DefaultHeaderContent />;
  }
}

function CustomHeaderContentByPaymentType({ paymentType }: { paymentType: string }): Node {
  switch (paymentType) {
    case 'externalpayprovider':
    case 'ewallet':
      return <CustomHeaderContent />;
    default:
      return <DefaultHeaderContent />;
  }
}

function CustomHeaderContent(): Node {
  return (<>
    <PayingBy>
      <PayingByMessage />
      <EbanxLogoNew src={ebanxLogoNewSrc} alt="ebanx" />
      <PartnerLogo />
    </PayingBy>
    <SecurePageSpanNew>
      <MobileHidden>
        <SafePageMessage />
      </MobileHidden>
    </SecurePageSpanNew>
  </>);
}

function DefaultHeaderContent(): Node {

  return (<>
    <PayingBy>
      <PayingByMessage />
      <EbanxLogo src={ebanxDarkBlueSrc} alt="ebanx" />
      <PartnerLogo />
    </PayingBy>
    <SecurePageSpan>
      <MobileHidden>
        <SafePageMessage />
      </MobileHidden>
    </SecurePageSpan>
  </>);
}

const MerchantLogo = styled.img`
  height: 34px;
  max-width: 100%;
  margin: auto;
  display: block;
`;

const PartnerSvg = styled.img`
  height: 1em;
  max-width: 100%;
  display: block;
  margin: auto;
  margin-top: 6px;
  margin-left: 6px;
`;

const ContentWrapper = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
`;

const PayingBy = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const EbanxLogo = styled.img`
  height: 0.8em;
  max-width: 100%;
  display: block;

  [dir='ltr'] & {
    margin-left: 6px;
  }

  [dir='rtl'] & {
    margin-right: 6px;
  }
`;

const EbanxLogoNew = styled.img`
  height: 2em;
  max-width: 100%;
  display: block;

  [dir='ltr'] & {
    margin-left: 6px;
  }

  [dir='rtl'] & {
    margin-right: 6px;
  }
`;

const SecurePageSpan = styled.span`
  color: ${props => props.theme.colors.new_primary[700]};
  &:before {
    content: '';
    padding: 0.5em;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${padlockNewSrc});

    [dir='ltr'] & {
      margin-right: 0.3rem;
    }

    [dir='rtl'] & {
      margin-left: 0.3rem;
    }
  }
`;

const SecurePageSpanNew = styled.span`
  color: ${props => props.theme.colors.green(48)};
  &:before {
    content: '';
    padding: 0.5em;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${padlock});

    [dir='ltr'] & {
      margin-right: 0.3rem;
    }

    [dir='rtl'] & {
      margin-left: 0.3rem;
    }
  }
`;

const MobileHidden = styled.span`
  @media (max-width: 840px) {
    display: none;
  }
`;
